// Public API of @/domains/shared/react-openlayers/Map
//
// Compound component `Map` is the single React-component entry point.
// Non-component symbols (hooks, utils, types, contexts, constants, OL
// re-exports, icons) are exported flat. See openspec change
// `implement-compound-map-modules` design.md.

// ─── Compound root + children ─────────────────────────────────────────

import { MapContainer } from "./MapContainer";

// Provider/context components
import { FeaturesStore } from "./FeaturesStore";
import { SelectedFeatureStore } from "./SelectedFeatureStore";
import { InnerText } from "./Text";
import { SyncMap, SyncMapGroup } from "./SyncMapGroup";
import { ImageMapForPixel } from "./ImageMapForPixel";

// Layer components
import {
  TileLayer,
  GeoJsonLayer,
  StyledGeoJsonLayer,
  LayerGroup,
  ImageOverlay,
  SelectedFeature,
  CustomMarker,
  CustomCircle,
  CustomPolyLine,
  CustomPolygon,
  CustomRectangle,
  CustomMultiPoint,
  ImageMarker,
  TextMarker,
  BoundaryCircle,
} from "./layer";

// Control components
import {
  CompassWheel,
  DrawingTools,
  FullScreenFeature,
  ZoomFeature,
  ControlSection,
  ControlGroup,
} from "./control";

// Interaction components (headless — DOM 미렌더)
import { PolygonDrawInteraction } from "./interaction/PolygonDrawInteraction";
import { BoxDrawInteraction } from "./interaction/BoxDrawInteraction";

export const Map = Object.assign(MapContainer, {
  // Stores / context providers
  FeaturesStore,
  SelectedFeatureStore,
  SyncMapGroup,
  SyncMap,
  // Text
  Text: InnerText,
  InnerText,
  // Image-based map (stand-alone)
  ImageMapForPixel,
  // Layers
  TileLayer,
  GeoJsonLayer,
  StyledGeoJsonLayer,
  LayerGroup,
  ImageOverlay,
  SelectedFeature,
  // Annotations
  CustomMarker,
  CustomCircle,
  CustomPolyLine,
  CustomPolygon,
  CustomRectangle,
  CustomMultiPoint,
  ImageMarker,
  TextMarker,
  BoundaryCircle,
  // Controls
  CompassWheel,
  DrawingTools,
  FullScreenFeature,
  ZoomFeature,
  ControlSection,
  ControlGroup,
  // Interactions (headless)
  PolygonDrawInteraction,
  BoxDrawInteraction,
});

// ─── Flat non-component exports ───────────────────────────────────────

// Hooks + utils barrels (include icons/constants/contexts/helpers)
export * from "./hooks";
export * from "./utils";
export * from "./constants";

// Non-component symbols from component files (types, contexts, hooks)
export type { Lng, Lat, Location, MapProps } from "./MapContainer";
export type {
  FeatureFromGeojson,
  FeatureCollection,
} from "./FeaturesStore";
export { FeaturesStoreContext, useFeaturesStore } from "./FeaturesStore";
// useFeatureStore is already re-exported via ./hooks
export type { InnerTextProps } from "./Text";
export { FeatureContext } from "./FeatureContext";
export { MapContext } from "./MapContext";

// NOTE: During Phase 8/9 transition, flat component exports are kept
// alongside the compound `Map` for backward compat. Consumers updated
// via codemod migrate to `Map.X`. Flat exports removed in Phase 13.
export * from "./layer";
export * from "./control";
export * from "./MapContainer"; // exports `MapContainer` as an alias during migration
export * from "./SyncMapGroup";
export * from "./ImageMapForPixel";
export * from "./Text";
export * from "./SelectedFeatureStore";
export * from "./FeaturesStore";
export * from "./interaction/PolygonDrawInteraction";
export * from "./interaction/BoxDrawInteraction";

// ─── OpenLayers library re-exports (flat) ─────────────────────────────

export { fromLonLat, toLonLat } from "ol/proj";
export type { Coordinate as OlCoordinate } from "ol/coordinate";
export type { Extent } from "ol/extent";
export type { default as OlMap } from "ol/Map";
export type { default as MapBrowserEvent } from "ol/MapBrowserEvent";
export type { ModifyEvent } from "ol/interaction/Modify";
export {
  Style,
  Stroke,
  Fill,
  Icon,
  Text,
  Circle as CircleStyle,
  RegularShape,
} from "ol/style";
export type { StyleFunction } from "ol/style/Style";
export { default as Overlay } from "ol/Overlay";
export { default as OlFeature } from "ol/Feature";
export { default as OlVectorLayer } from "ol/layer/Vector";
export { default as OlVectorSource } from "ol/source/Vector";
export { LineString, MultiLineString, Point, Polygon } from "ol/geom";
/** extent → 직사각형 Polygon. 이름으로 동작이 드러나도록 재명명해 내보낸다. */
export { fromExtent as polygonFromExtent } from "ol/geom/Polygon";
